import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  FootprintPolygonsType,
  LevelType,
  PositionType,
  StructureImageTransformType
} from 'data/types';
import { polygonBoundingBox } from 'utils/footprint';
import { getLevelFootprintArea } from 'utils/footprint-polygons';
import {
  calculateAutoScaleRatio,
  scaleFootprintPoints
} from 'utils/footprint-scaling';
import { getEffectiveFootprintShapes } from 'utils/footprint-union';
import {
  countsTowardFootprintSquareFootage,
  isLevelConditioned
} from 'utils/structure';

export function getFirstMainLevelIndex(structure: LevelType[]): number {
  const index = structure.findIndex((level) => level.type === 'level');
  return Math.max(index, 0);
}

export function getConditionedLevelCount(structure: LevelType[]): number {
  return structure.filter((level) => isLevelConditioned(level)).length;
}

export function getConditionedFootprintAreas(structure: LevelType[]): number[] {
  return structure
    .filter((level) => isLevelConditioned(level))
    .map((level) => getLevelFootprintArea(level));
}

export function getTotalFootprintArea(structure: LevelType[]): number {
  return structure.reduce((sum, level) => {
    if (!countsTowardFootprintSquareFootage(level)) return sum;
    return sum + getLevelFootprintArea(level);
  }, 0);
}

export function getConditionedLevels(structure: LevelType[]): LevelType[] {
  return structure.filter((level) => isLevelConditioned(level));
}

export function computeFootprintCentroid(points: PositionType[]): PositionType {
  const sum = points.reduce(
    (acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }),
    { x: 0, y: 0 }
  );
  return {
    x: sum.x / points.length,
    y: sum.y / points.length
  };
}

export function getFootprintBboxCenter(
  footprint: PositionType[]
): PositionType {
  const bb = polygonBoundingBox(footprint);
  return {
    x: (bb.minX + bb.maxX) / 2,
    y: (bb.minY + bb.maxY) / 2
  };
}

type AutoScalePivotInput = {
  footprintPolygons: FootprintPolygonsType;
  imageTransform: StructureImageTransformType | undefined;
  imageDims: { naturalWidth: number; naturalHeight: number } | null;
};

export function computeAutoScalePivot({
  footprintPolygons,
  imageTransform,
  imageDims
}: AutoScalePivotInput): PositionType {
  if (imageTransform && imageDims) {
    const posX = imageTransform.startPositionX ?? 0;
    const posY = imageTransform.startPositionY ?? 0;
    return {
      x: (posX + imageDims.naturalWidth / 2) / STRUCTURE_GRID_SIZE,
      y: (posY + imageDims.naturalHeight / 2) / STRUCTURE_GRID_SIZE
    };
  }
  return computeFootprintCentroid(footprintPolygons.flat());
}

type ResolveAutoScalePivotInput = {
  footprintPolygons: FootprintPolygonsType;
  layoutImageAttachmentId: number | null;
  imageTransform: StructureImageTransformType | undefined;
  imageDims: { naturalWidth: number; naturalHeight: number } | null;
};

export function resolveAutoScalePivot({
  footprintPolygons,
  layoutImageAttachmentId,
  imageTransform,
  imageDims
}: ResolveAutoScalePivotInput): PositionType {
  const hasActiveImage =
    layoutImageAttachmentId !== null && !!imageTransform && !!imageDims;
  return computeAutoScalePivot({
    footprintPolygons,
    imageTransform: hasActiveImage ? imageTransform : undefined,
    imageDims: hasActiveImage ? imageDims : null
  });
}

type AutoScaleParamsInput = {
  conditionedAreas: number[];
  totalSqft: number;
};

type AutoScaleParams = {
  ratio: number;
  totalConditionedArea: number;
};

export function computeAutoScaleParams({
  conditionedAreas,
  totalSqft
}: AutoScaleParamsInput): AutoScaleParams {
  const totalConditionedArea = conditionedAreas.reduce((sum, a) => sum + a, 0);
  const ratio =
    totalConditionedArea > 0 && totalSqft > 0
      ? Math.sqrt(totalSqft / totalConditionedArea)
      : 1;
  return { ratio, totalConditionedArea };
}

export const AUTO_SCALE_RECOMMENDED_TOLERANCE = 0.02;

export function isFootprintAtRecommendedScale(ratio: number): boolean {
  return Math.abs(ratio - 1) <= AUTO_SCALE_RECOMMENDED_TOLERANCE;
}

export function shouldShowAutoScaleForArea(
  currentArea: number,
  targetSqft: number
): boolean {
  if (currentArea <= 0 || targetSqft <= 0) return false;
  return !isFootprintAtRecommendedScale(
    calculateAutoScaleRatio(currentArea, targetSqft)
  );
}

export function shouldAutoScaleFootprintOnStepEntry(params: {
  isFootprintStep: boolean;
  alreadyRan: boolean;
  canAutoScale: boolean;
}): boolean {
  return params.isFootprintStep && !params.alreadyRan && params.canAutoScale;
}

export type FootprintStepEntryScaleAction =
  | 'scale-footprint'
  | 'scale-images'
  | 'wait'
  | 'none';

export function resolveFootprintStepEntryScaleAction(params: {
  isFootprintStep: boolean;
  alreadyRanFootprint: boolean;
  alreadyRanImages: boolean;
  canAutoScaleFootprint: boolean;
  isOnFirstMainLevel: boolean;
  hasLevelLayoutImages: boolean;
  totalSqft: number;
}): FootprintStepEntryScaleAction {
  if (!params.isFootprintStep) {
    return 'none';
  }

  if (
    shouldAutoScaleFootprintOnStepEntry({
      isFootprintStep: true,
      alreadyRan: params.alreadyRanFootprint,
      canAutoScale: params.canAutoScaleFootprint
    })
  ) {
    return 'scale-footprint';
  }

  if (params.alreadyRanImages) {
    return 'none';
  }

  if (!params.isOnFirstMainLevel) {
    return 'wait';
  }

  if (params.hasLevelLayoutImages && params.totalSqft > 0) {
    return 'scale-images';
  }

  return 'none';
}

type WouldAutoScaleChangeInput = {
  levels: LevelType[];
  ratio: number;
  pivot: PositionType;
};

export function wouldAutoScaleChangeAnyFootprint({
  levels,
  ratio,
  pivot
}: WouldAutoScaleChangeInput): boolean {
  if (ratio === 1) return false;
  for (const level of levels) {
    for (const shape of getEffectiveFootprintShapes(level)) {
      const scaled = scaleFootprintPoints(shape.points, ratio, pivot);
      for (let i = 0; i < scaled.length; i += 1) {
        if (
          Math.round(scaled[i].x) !== shape.points[i].x ||
          Math.round(scaled[i].y) !== shape.points[i].y
        ) {
          return true;
        }
      }
    }
  }
  return false;
}
